const num = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const str = (value, fallback = '') => (value == null ? fallback : String(value));

const sliceDate = (value) => (value ? String(value).slice(0, 10) : '');

function hasModernShape(payload) {
  return Boolean(payload && typeof payload === 'object' && payload.kpis && typeof payload.kpis === 'object');
}

function normalizeInventoryLegacy(data) {
  const products = data.products || {};
  const todayMovements = data.todayMovements || {};
  const warehouses = Array.isArray(data.warehouses) ? data.warehouses : [];
  const recentMovements = Array.isArray(data.recentMovements) ? data.recentMovements : [];

  return {
    kpis: {
      totalValue: num(products.total_stock_value),
      warehouses: warehouses.length || num(products.total_products),
      movementsToday: num(todayMovements.total_movements),
      lowStock: num(products.low_stock),
    },
    chartRows: warehouses.slice(0, 6).map((w) => ({
      label: str(w.name, 'مستودع'),
      value: num(w.current_stock),
    })),
    recentItems: recentMovements.slice(0, 5).map((m) => ({
      id: m.id,
      label: str(m.reference || m.product_name || m.movement_type, 'حركة'),
      meta: str(m.warehouse_name || m.product_name),
      date: sliceDate(m.date || m.created_at),
    })),
  };
}

function normalizeVentesLegacy(data) {
  const orders = data.orders || {};
  const deliveries = data.deliveries || {};
  const revenue = data.revenue || {};
  const ordersByStatus = Array.isArray(data.ordersByStatus) ? data.ordersByStatus : [];
  const recentOrders = Array.isArray(data.recentOrders) ? data.recentOrders : [];

  const openOrders = num(orders.pending) + num(orders.confirmed);
  const pendingDeliveries = num(deliveries.pending) + num(deliveries.inTransit);
  const totalOrders = num(orders.total);
  const conversionRate = totalOrders > 0 ? Math.round((num(orders.delivered) / totalOrders) * 100) : 0;

  return {
    kpis: {
      monthRevenue: num(revenue.total_revenue),
      openOrders,
      pendingDeliveries,
      conversionRate,
    },
    chartRows: ordersByStatus.map((row) => ({
      label: str(row.status, 'unknown'),
      value: num(row.count),
    })),
    recentItems: recentOrders.slice(0, 5).map((o) => ({
      id: o.id,
      label: str(o.order_number, 'أمر'),
      meta: str(o.customer_name),
      date: sliceDate(o.order_date || o.created_at),
    })),
  };
}

function normalizeFacturationLegacy(data) {
  const statusStats = Array.isArray(data.statusStats) ? data.statusStats : [];
  const recentDocuments = Array.isArray(data.recentDocuments) ? data.recentDocuments : [];
  const revenue = data.revenue || {};
  const pending = data.pending || {};

  const totalInvoiced = num(revenue.total_revenue);
  const outstanding = num(pending.pending_amount);
  const overdue = statusStats.find((s) => s.status === 'pending')?.count ?? pending.pending_count ?? 0;
  const paidCount = num(revenue.paid_count);
  const totalCount = statusStats.reduce((sum, s) => sum + num(s.count), 0);
  const collectionRate = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

  return {
    kpis: {
      totalInvoiced,
      outstanding,
      overdue: num(overdue),
      collectionRate,
    },
    chartRows: statusStats.slice(0, 6).map((s) => ({
      label: str(s.status),
      value: num(s.count),
    })),
    recentItems: recentDocuments.slice(0, 5).map((d) => ({
      id: d.id,
      label: str(d.number || d.title, 'مستند'),
      meta: str(d.client),
      date: sliceDate(d.date),
    })),
  };
}

function normalizeCrmLegacy(data) {
  return {
    kpis: {
      pipelineValue: num(data.totalValue),
      totalClients: num(data.totalContacts),
      activeLeads: num(data.totalOpportunities),
      activeCampaigns: num(data.pendingActivities),
    },
    chartRows: [
      { label: 'فرص', value: num(data.totalOpportunities) },
      { label: 'صفقات رابحة', value: num(data.wonDeals) },
      { label: 'عملاء نشطون', value: num(data.activeContacts) },
    ],
    recentItems: [],
  };
}

function normalizeAchatsLegacy(data) {
  const orders = data.orders || {};
  const requests = data.requests || {};
  const suppliers = data.suppliers || {};
  const spending = data.spending || {};
  const recentOrders = Array.isArray(data.recentOrders) ? data.recentOrders : [];

  return {
    kpis: {
      monthSpend: num(spending.total_spending || orders.total_spending),
      pendingRequests: num(requests.pending_requests),
      activeOrders: num(orders.pending_orders) + num(orders.received_orders),
      suppliers: num(suppliers.total || suppliers.active),
    },
    chartRows: [
      { label: 'طلبات معلقة', value: num(requests.pending_requests) },
      { label: 'طلبات معتمدة', value: num(requests.approved_requests) },
      { label: 'أوامر مستلمة', value: num(orders.received_orders) },
    ],
    recentItems: recentOrders.slice(0, 5).map((o) => ({
      id: o.id,
      label: str(o.order_number || o.reference, 'أمر'),
      meta: str(o.supplier_name),
      date: sliceDate(o.order_date || o.created_at),
    })),
  };
}

function normalizeHrLegacy(data) {
  const employees = data.employees || {};
  const recruitment = data.recruitment || {};
  const training = data.training || {};
  const leaves = data.leaves || {};
  const deptRows = Array.isArray(employees.departmentDistribution) ? employees.departmentDistribution : [];

  return {
    kpis: {
      totalEmployees: num(employees.total),
      onLeaveToday: num(leaves.currentMonth?.count || leaves.totalPending),
      openPositions: num(recruitment.stats?.nouveau || recruitment.stats?.entretien),
      trainingActive: num(training.activeSessions || training.total),
    },
    chartRows: deptRows.slice(0, 6).map((d) => ({
      label: str(d.department, 'قسم'),
      value: num(d.count),
    })),
    recentItems: [],
  };
}

function normalizeTresorerieLegacy(data) {
  const caisses = data.caisses || {};
  const today = data.today || {};
  const month = data.month || {};
  const recentOperations = Array.isArray(data.recentOperations) ? data.recentOperations : [];
  const balanceByCaisse = Array.isArray(data.balanceByCaisse) ? data.balanceByCaisse : [];

  return {
    kpis: {
      cashOnHand: num(caisses.totalbalance),
      todayReceipts: num(today.totalreceipts),
      todayExpenses: num(today.totalpayments),
      safeBalance: num(caisses.totalbalance),
    },
    chartRows: (balanceByCaisse.length ? balanceByCaisse : [{ label: 'عمليات الشهر', value: num(month.totaloperations) }])
      .slice(0, 6)
      .map((row) => ({
        label: str(row.label || row.name || row.caisse_name, 'صندوق'),
        value: num(row.balance ?? row.value ?? row.amount),
      })),
    recentItems: recentOperations.slice(0, 5).map((op) => ({
      id: op.id,
      label: str(op.reference || op.description, 'عملية'),
      meta: str(op.category || op.payee_payer),
      date: sliceDate(op.operation_date || op.created_at),
    })),
  };
}

function normalizeComptabiliteLegacy(data) {
  const stats = data.stats || {};
  const accountClasses = Array.isArray(data.account_classes) ? data.account_classes : [];
  const recentEntries = Array.isArray(data.recent_entries) ? data.recent_entries : [];

  return {
    kpis: {
      currentBalance: num(data.kpis?.currentBalance ?? stats.net_profit),
      entriesMonth: num(data.kpis?.entriesMonth ?? stats.total_entries),
      pendingApprovals: num(data.kpis?.pendingApprovals ?? stats.pending_entries),
      validatedEntries: num(data.kpis?.validatedEntries ?? stats.validated_entries),
      totalAccounts: num(data.kpis?.totalAccounts ?? stats.total_accounts),
    },
    chartRows: accountClasses.slice(0, 6).map((c) => ({
      label: str(c.class_name || c.name || c.label, 'حساب'),
      value: num(c.count ?? c.total),
    })),
    recentItems: recentEntries.slice(0, 5).map((e) => ({
      id: e.id,
      label: str(e.entry_number || e.reference, 'قيد'),
      meta: str(e.description),
      date: sliceDate(e.entry_date || e.created_at),
    })),
  };
}

function normalizeBankingLegacy(data) {
  const accounts = data.accounts || {};
  const reconciliations = data.reconciliations || {};
  const pendingChecks = data.pendingChecks || {};
  const recentTransactions = Array.isArray(data.recentTransactions) ? data.recentTransactions : [];
  const balanceByAccount = Array.isArray(data.balanceByAccount) ? data.balanceByAccount : [];

  return {
    kpis: {
      totalBalance: num(accounts.totalBalance),
      accountsCount: num(accounts.totalAccounts || accounts.activeAccounts),
      pendingReconciliations: num(reconciliations.pendingReconciliations),
      checksOutstanding: num(pendingChecks.total || pendingChecks.pending),
    },
    chartRows: (balanceByAccount.length ? balanceByAccount : [{ label: 'حسابات نشطة', value: num(accounts.activeAccounts) }])
      .slice(0, 6)
      .map((row) => ({
        label: str(row.account_name || row.label || row.name, 'حساب'),
        value: num(row.balance ?? row.value),
      })),
    recentItems: recentTransactions.slice(0, 5).map((tx) => ({
      id: tx.id,
      label: str(tx.description || tx.reference, 'عملية'),
      meta: str(tx.transaction_type || tx.type),
      date: sliceDate(tx.transaction_date || tx.statement_date),
    })),
  };
}

function normalizeEmployeePortalLegacy(data) {
  const stats = data.stats || {};
  const recentRequests = Array.isArray(data.recentRequests) ? data.recentRequests : [];
  const recentAnnouncements = Array.isArray(data.recentAnnouncements) ? data.recentAnnouncements : [];

  return {
    kpis: {
      leaveBalance: num(stats.pendingLeaves),
      pendingRequests: num(stats.pendingRequests),
      trainingHours: num(stats.documents),
      performanceScore: num(stats.activeContracts),
    },
    chartRows: [
      { label: 'طلبات معلقة', value: num(stats.pendingRequests) },
      { label: 'إجازات معلقة', value: num(stats.pendingLeaves) },
      { label: 'إعلانات', value: num(stats.publishedAnnouncements) },
    ],
    recentItems: (recentAnnouncements.length ? recentAnnouncements : recentRequests)
      .slice(0, 5)
      .map((item) => ({
        id: item.id,
        label: str(item.title || item.request_code, 'عنصر'),
        meta: str(item.status),
        date: sliceDate(item.created_at || item.published_at),
      })),
  };
}

const LEGACY_NORMALIZERS = {
  '/inventaire/dashboard': normalizeInventoryLegacy,
  '/inventory/dashboard': normalizeInventoryLegacy,
  '/ventes/dashboard': normalizeVentesLegacy,
  '/facturation/dashboard': normalizeFacturationLegacy,
  '/crm/dashboard': normalizeCrmLegacy,
  '/achats/dashboard': normalizeAchatsLegacy,
  '/hr/dashboard': normalizeHrLegacy,
  '/tresorerie/dashboard': normalizeTresorerieLegacy,
  '/comptabilite/dashboard': normalizeComptabiliteLegacy,
  '/banque/dashboard': normalizeBankingLegacy,
  '/banking/dashboard': normalizeBankingLegacy,
  '/employee-portal/dashboard': normalizeEmployeePortalLegacy,
};

export const DASHBOARD_ENDPOINT_FALLBACKS = {
  '/inventaire/dashboard': ['/inventory/dashboard'],
  '/banque/dashboard': ['/banking/dashboard'],
  '/pointage/dashboard': ['/timetracking/dashboard'],
  '/approbations/dashboard': ['/api/approbations/dashboard'],
  '/employee-portal/dashboard': ['/api/employee-portal/dashboard'],
};

export function normalizeDashboardPayload(endpoint, payload) {
  if (!payload || typeof payload !== 'object') {
    return { kpis: {}, chartRows: [], recentItems: [] };
  }

  if (hasModernShape(payload)) {
    return {
      kpis: payload.kpis || {},
      chartRows: Array.isArray(payload.chartRows) ? payload.chartRows : [],
      movementTrend30Days: Array.isArray(payload.movementTrend30Days) ? payload.movementTrend30Days : [],
      catalogBreakdown: Array.isArray(payload.catalogBreakdown) ? payload.catalogBreakdown : [],
      recentItems: Array.isArray(payload.recentItems) ? payload.recentItems : [],
    };
  }

  const normalize = LEGACY_NORMALIZERS[endpoint];
  if (normalize) {
    return normalize(payload);
  }

  return { kpis: {}, chartRows: [], recentItems: [] };
}

function countByField(rows, field) {
  const map = new Map();
  rows.forEach((row) => {
    const key = str(row[field], 'unknown');
    map.set(key, (map.get(key) || 0) + 1);
  });
  return [...map.entries()].map(([label, value]) => ({ label, value }));
}

export async function fetchSyntheticDashboard(api, endpoint) {
  if (endpoint === '/articles/dashboard') {
    const [products, services, categories, variantes] = await Promise.all([
      api.get('/facturation/products').catch(() => []),
      api.get('/services').catch(() => []),
      api.get('/categories').catch(() => []),
      api.get('/articles/variantes').catch(() => []),
    ]);
    const productRows = Array.isArray(products) ? products : [];
    const serviceRows = Array.isArray(services) ? services : [];
    const categoryRows = Array.isArray(categories) ? categories : [];
    const variantRows = Array.isArray(variantes) ? variantes : [];

    const onlyProducts = productRows.filter((p) => !p.type || p.type === 'product');
    const catalogValue =
      onlyProducts.reduce((sum, p) => sum + num(p.price), 0)
      + serviceRows.reduce((sum, s) => sum + num(s.price), 0);
    const lowStock = onlyProducts.filter(
      (p) => num(p.stock ?? p.stock_quantity) <= num(p.min_stock, 0) && num(p.min_stock, 0) > 0,
    ).length;
    const activeProducts = onlyProducts.filter(
      (p) => ['active', 'actif'].includes(str(p.status).toLowerCase()),
    ).length;
    const activeServices = serviceRows.filter(
      (s) => ['active', 'actif'].includes(str(s.status).toLowerCase()),
    ).length;

    const totalItems = onlyProducts.length + serviceRows.length;
    const activeItems = activeProducts + activeServices;
    const allCatalogRows = [...onlyProducts, ...serviceRows].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    );

    return {
      kpis: {
        catalogValue,
        productsValue: onlyProducts.reduce((sum, p) => sum + num(p.price), 0),
        servicesValue: serviceRows.reduce((sum, s) => sum + num(s.price), 0),
        totalProducts: onlyProducts.length,
        totalServices: serviceRows.length,
        totalItems,
        totalCategories: categoryRows.length,
        totalVariants: variantRows.length,
        totalUnits: 0,
        lowStock,
        activeProducts,
        activeServices,
        activeItems,
        inactiveItems: Math.max(0, totalItems - activeItems),
      },
      chartRows: countByField(onlyProducts, 'category').slice(0, 8),
      catalogBreakdown: [
        { label: 'products', value: onlyProducts.length },
        { label: 'services', value: serviceRows.length },
        { label: 'variants', value: variantRows.length },
      ],
      recentItems: allCatalogRows.slice(0, 6).map((p) => ({
        id: p.id,
        label: str(p.name, 'منتج'),
        meta: str(p.reference || p.sku),
        date: sliceDate(p.created_at || p.updated_at),
      })),
    };
  }

  if (endpoint === '/salaires/dashboard') {
    const [payslips, advances, transfers] = await Promise.all([
      api.get('/salaries/payslips').catch(() => []),
      api.get('/salaries/advances-loans').catch(() => []),
      api.get('/salaires/virements-bancaires').catch(() => []),
    ]);
    const payslipRows = Array.isArray(payslips) ? payslips : [];
    const advanceRows = Array.isArray(advances) ? advances : [];
    const transferRows = Array.isArray(transfers) ? transfers : [];
    const totalPayroll = payslipRows.reduce((sum, p) => sum + num(p.net_salary || p.total), 0);
    const avgSalary = payslipRows.length ? Math.round(totalPayroll / payslipRows.length) : 0;
    const activeLoans = advanceRows.filter((a) => ['active', 'ongoing', 'actif'].includes(str(a.status).toLowerCase())).length;
    const pendingTransfers = transferRows.filter((t) => ['pending', 'processing', 'en_attente'].includes(str(t.status).toLowerCase())).length;

    return {
      kpis: { totalPayroll, activeLoans, pendingTransfers, avgSalary },
      chartRows: countByField(payslipRows, 'status').slice(0, 6),
      recentItems: payslipRows.slice(0, 5).map((p) => ({
        id: p.id,
        label: str(p.employee_name, 'موظف'),
        meta: str(p.net_salary || p.total),
        date: sliceDate(p.created_at),
      })),
    };
  }

  if (endpoint === '/pointage/dashboard' || endpoint === '/timetracking/dashboard') {
    const today = new Date().toISOString().slice(0, 10);
    const [stats, entries] = await Promise.all([
      api.get('/timetracking/daily/stats', { date: today }).catch(() => ({})),
      api.get('/timetracking/daily', { date: today }).catch(() => []),
    ]);
    const entryRows = Array.isArray(entries) ? entries : [];
    const statRow = stats && typeof stats === 'object' ? stats : {};

    return {
      kpis: {
        presentToday: num(statRow.present),
        lateArrivals: num(statRow.late),
        overtimeHours: num(statRow.totalHours),
        absentees: num(String(statRow.absent || '').replace(/\D/g, '') || statRow.absent),
      },
      chartRows: countByField(entryRows, 'status_label').slice(0, 6),
      recentItems: entryRows.slice(0, 5).map((e) => ({
        id: e.id,
        label: str(e.employee_name, 'موظف'),
        meta: str(e.status_label || e.status),
        date: sliceDate(e.check_in || e.date),
      })),
    };
  }

  if (endpoint === '/approbations/dashboard') {
    const [requests, workflows] = await Promise.all([
      api.get('/approbations/requests').catch(() => []),
      api.get('/approbations/workflows').catch(() => []),
    ]);
    const requestRows = Array.isArray(requests) ? requests : [];
    const workflowRows = Array.isArray(workflows) ? workflows : [];
    const pendingApproval = requestRows.filter((r) =>
      ['en_attente', 'en_cours', 'pending', 'in_progress'].includes(String(r.status || '').toLowerCase()),
    ).length;
    const activeWorkflows = workflowRows.filter((w) =>
      String(w.status || '').toLowerCase() === 'actif' || String(w.status || '').toLowerCase() === 'active',
    ).length;
    const rejectedWeek = requestRows.filter((r) =>
      ['rejete', 'rejected'].includes(String(r.status || '').toLowerCase()),
    ).length;

    return {
      kpis: {
        pendingApproval,
        activeWorkflows,
        avgApprovalTime: requestRows.length ? Math.round((pendingApproval / requestRows.length) * 100) : 0,
        rejectedWeek,
      },
      chartRows: countByField(requestRows, 'status').slice(0, 6),
      recentItems: requestRows.slice(0, 5).map((r) => ({
        id: r.id,
        label: str(r.title || r.request_code, 'طلب'),
        meta: str(r.requester_name),
        date: sliceDate(r.created_at),
      })),
    };
  }

  return null;
}
